import { t } from "i18next";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useFetch } from "../../../../../hooks/useFetch";
import { TriggerType } from "../../../../../types/types";
import { Label } from "../../../../../ui/Label/Label";
import { SelectButton } from "../../../../../ui/SelectButton/SelectButton";
import { ScrollArea } from "../../../../../ui/ScrollArea/ScrollArea";
import { Button } from "../../../../../ui/Button";
import { Input } from "../../../../../ui/Input/Input";
import { equipmentStatuses, globalTriggerTypes } from "../../../../../enums";
import { useOutletContext } from "react-router-dom";
import DeleteTrigger from "../../DeleteTrigger";
import { Switch, SwitchSM } from "../../../../../ui/Switch/Switch";
import { RequestDataType } from "../../AddTrigger";
import { useAppStore } from "../../../../../stores/RootStore";
import ErrorsBlock from "../../../../../components/errors/ErrorsBlock";

type Props = {
  id: string | undefined;
  currentTrigger: TriggerType | undefined;
  closeEditModal: Function;
};

export const toTempType = (type: string) => {
  switch (type) {
    case "Sites::Triggers::RfidIn":
      return "rfid_in";
    case "Sites::Triggers::RfidOut":
      return "rfid_out";
    case "Sites::Triggers::RfidSwitch":
      return "rfid_switch";
    default:
      return "";
  }
};

const ScreenSettingsTab = ({ id, currentTrigger, closeEditModal }: Props) => {
  const store = useAppStore()
  const [triggers, setTriggers] = useOutletContext<[TriggerType[], Function]>();
  const statusChangedToList = equipmentStatuses.map((status) => {
    return { value: status, label: t(status) };
  });

  let [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [errors, setErrors] = useState([]);

  const { handleSubmit, control, watch } = useForm({
    defaultValues: {
      name: currentTrigger?.attributes.name,
      type: toTempType(currentTrigger?.attributes.type!),
      enabled: currentTrigger?.attributes.enabled,
      min_triggered_period: currentTrigger?.attributes.min_triggered_period,
      status_change_to: currentTrigger?.attributes.status_change_to,
      value_more_than: currentTrigger?.attributes.value_more_than ? currentTrigger?.attributes.value_more_than : "",
      value_less_than: currentTrigger?.attributes.value_less_than ? currentTrigger?.attributes.value_less_than : "",
      card: currentTrigger?.attributes.string_value_equal ? currentTrigger?.attributes.string_value_equal : "",
    },
  });

  const isTypeIs = (type: "rfid" | "value_less" | "value_more") => {
    const typeValue = watch("type");
    switch (type) {
      case "rfid":
        const isRfid = typeValue == "rfid_in" || typeValue == "rfid_out" || typeValue == "rfid_switch";
        return isRfid;
    }
  };

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  // Обновление информации о триггере
  const [{ response: saveResponse, isLoading: saveIsLoading, error: saveError }, doFetchItemSave] = useFetch(
    `/directories/triggers/${id}`
  );

  let requestData: RequestDataType;
  const onSubmit = (formData: any) => {
    setErrors([]);
    requestData = {
      name: formData.name,
      min_triggered_period: Number(formData.min_triggered_period),
      enabled: formData.enabled,
      status_change_to: formData.status_change_to,
    };

    switch (formData.type) {
      //@ts-ignore
      case "rfid_switch":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidSwitch";
      //@ts-ignore
      case "rfid_out":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidOut";
      case "rfid_in":
        if (!requestData.type) requestData.type = "Sites::Triggers::RfidIn";
        requestData.string_value_equal = formData.card;
        break;
    }

    doFetchItemSave({
      method: "PUT",
      data: {
        data: requestData,
      },
    });
  };

  const editTrigger = (itemSaveResponse: { data: unknown }) => {
    const newScreenData = itemSaveResponse.data as unknown as TriggerType;
    const withoutCurrentItem = triggers?.filter((trigger) => trigger.id != currentTrigger?.id)!;
    let newTriggers = [...withoutCurrentItem, newScreenData]
    store.setTriggers(newTriggers)
    setTriggers(newTriggers);
  };

  useEffect(() => {
    if (!saveResponse) return;
    
    editTrigger(saveResponse);
    closeEditModal();
  }, [saveResponse]);

  useEffect(() => {
    if (saveError) setErrors(saveError.errors);
  }, [saveError]);

  return (
    <>
      <ScrollArea className="w-full">
        <div className="flex flex-col w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col relative space-y-1">
                <Controller
                  name="enabled"
                  control={control}
                  render={({ field }) => (
                    <div className="relative">
                      <div className="flex justify-between items-center py-2">
                        <Label htmlFor="enabled">{field.value ? <>{t("current_trigger_state")} - {t("trigger_enabled")}</> : <>{t("current_trigger_state")} - {t("trigger_disabled")}</>}</Label>
                        <SwitchSM checked={field.value} onCheckedChange={field.onChange} id="enabled" />
                      </div>
                    </div>
                  )}
                />
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative space-y-2">
                      <Input {...field} label={`${t("trigger_name")}*`} />
                    </div>
                  )}
                />
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <div className="relative space-y-2">
                      <Label>{t("trigger_type")}*</Label>
                      <SelectButton
                        disabled={true}
                        label={"Выберите тип триггера"}
                        searchable={false}
                        options={globalTriggerTypes}
                        value={value ? globalTriggerTypes?.find((s) => s.value == String(value))! : null}
                        onChange={(e) => onChange(e?.value)}
                      />
                    </div>
                  )}
                />
                <Controller
                  name="min_triggered_period"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <div className="relative space-y-2">
                      <Input type="number" {...field} label={`${t("min_triggered_period")}*`} />
                    </div>
                  )}
                />
                {isTypeIs("rfid") && (
                  <Controller
                    name="card"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <div className="relative space-y-2">
                        <Input {...field} label={`${t("card")}*`} />
                      </div>
                    )}
                  />
                )}
                <Controller
                  name="status_change_to"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <div className="relative space-y-2">
                      <Label>{t("status_change_to")}*</Label>
                      <SelectButton
                        label={"Выберите изменение статуса"}
                        searchable={false}
                        options={statusChangedToList}
                        value={value ? statusChangedToList?.find((s) => s.value == String(value))! : null}
                        onChange={(e) => onChange(e?.value)}
                      />
                    </div>
                  )}
                />
              </div>

              <ErrorsBlock errors={errors} />
              <div className="mt-8 flex w-full justify-between">
                <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                  {t("delete")}
                </Button>
                <Button variant={"default"} size={"sm"} type="submit">
                  {t("save")}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </ScrollArea>
      <DeleteTrigger
        id={id!}
        isDeleteOpen={isDeleteOpen}
        closeModal={closeDeleteModal}
        closeEditModal={closeEditModal}
      />
    </>
  );
};

export default ScreenSettingsTab;
